import { IntlShape, defineMessages } from "@formatjs/intl"
import Utils from '@te/volt-utils'

const messages = defineMessages({
    durationYearPlural: {
        id: 'duration.years',
    },
    durationYearSingular: {
        id: 'duration.yearSingular',
    },
    durationMonthPlural: {
        id: 'duration.months',
    },
    durationMonthSingular: {
        id: 'duration.monthSingular',
    },
    durationWeekPlural: {
        id: 'duration.weeks',
    },
    durationWeekSingular: {
        id: 'duration.weekSingular',
    },
    durationDayPlural: {
        id: 'duration.days',
    },
    durationDaySingular: {
        id: 'duration.daySingular',
    },
    yearsUnit: {
        id: 'duration.years.unit',
    },
    monthsUnit: {
        id: 'duration.months.unit',
    },
    weeksUnit: {
        id: 'duration.weeks.unit',
    },
    daysUnit: {
        id: 'duration.days.unit',
    },
    yearSingularUnit: {
        id: 'duration.yearSingular.unit',
    },
    monthSingularUnit: {
        id: 'duration.monthSingular.unit',
    },
    weekSingularUnit: {
        id: 'duration.weekSingular.unit',
    },
    daySingularUnit: {
        id: 'duration.daySingular.unit',
    },
    offerLabelOneTimeCharge: {
        id: 'purchase.offerSelect.labelWithOneTimeCharge',
    },
    offerLabel: {
        id: 'purchase.offerSelect.label',
    },
    offerLabelWithCycleSingular: {
        id: 'purchase.offerSelect.labelWithCycle.singular',
    },
    offerLabelWithCyclePlural: {
        id: 'purchase.offerSelect.labelWithCycle.plural',
    },
    offerLabelWithDefinition: {
        id: 'purchase.offerSelect.withVideoDefinition.label',
    },
    offerLabelWithDefinitionWithCycleSingular: {
        id: 'purchase.offerSelect.withVideoDefinition.labelWithCycle.singular',
    },
    offerLabelWithDefinitionWithCyclePlural: {
        id: 'purchase.offerSelect.withVideoDefinition.labelWithCycle.plural',
    },
    hdFormat: {
        id: 'format.HD',
    },
    defaultFormat: {
        id: 'format.SD',
    },
    previewPrice: {
        id: 'offer.price.preview',
    },
    unknownPrice: {
        id: 'offer.price.unknown',
    },
    entitlementDate: {
        id: 'product.entitlementDate',
    },
    durationFull: {
        id: 'duration.full',
    },
    durationHoursPlural: {
        id: 'duration.hours',
    },
    durationMinutesPlural: {
        id: 'duration.minutes',
    },
    durationHourSingular: {
        id: 'duration.hourSingular',
    },
    durationMinuteSingular: {
        id: 'duration.minuteSingular',
    },
})

export const formatOfferUnit = (offer: { period: number, periodUnit: string}, intl: IntlShape, Models: any) => {
    const isPlural = offer.period > 1
    switch (offer.periodUnit) {
        case Models.Offer.UNIT.YEAR:
            return intl.formatMessage(isPlural ? messages.yearsUnit : messages.yearSingularUnit)
        case Models.Offer.UNIT.MONTH:
            return intl.formatMessage(isPlural ? messages.monthsUnit : messages.monthSingularUnit)
        case Models.Offer.UNIT.WEEK:
            return intl.formatMessage(isPlural ? messages.weeksUnit : messages.weekSingularUnit)
        case Models.Offer.UNIT.DAY:
            return intl.formatMessage(isPlural ? messages.daysUnit : messages.daySingularUnit)
        default:
            return null
    }
}

export const formatDuration2 = (duration: number, intl: IntlShape) => {
    if (!duration) return null

    const formatedDuration = Utils.date.getHoursAndMinutes(duration / 1000)

    return formatedDuration.hours
        ? formatedDuration.minutes
            ? intl.formatMessage(
                  messages.durationFull,
                  {
                      hours: formatedDuration.hours,
                      minutes: String(formatedDuration.minutes).padStart(2, '0'),
                  },
              )
            : intl.formatMessage(
                  formatedDuration.hours > 1 ? messages.durationHoursPlural : messages.durationHourSingular,
                  {
                      hours: formatedDuration.hours,
                  },
              )
        : formatedDuration.minutes
        ? intl.formatMessage(
              formatedDuration.minutes > 1
                  ? messages.durationMinutesPlural
                  : messages.durationMinuteSingular,
              {
                  minutes: String(formatedDuration.minutes).padStart(2, '0'),
              },
          )
        : null
}

const convertPeriodMs = (period: number, unit: any, Models: any) => {
    switch (unit) {
        case Models.Offer.UNIT.HOUR:
            return period * 3600 * 1000
        case Models.Offer.UNIT.MINUTE:
            return period * 60 * 1000
        case Models.Offer.UNIT.SECOND:
            return period * 1000
        case Models.Offer.UNIT.MS:
        default:
            return period
    }
}

export const formatOfferDuration = (offer: any, intl: IntlShape, Models: any) => {
    const isPlural = offer.period > 1
    const cycles = offer.cycles + 1
    const period = offer.period
    switch (offer.periodUnit) {
        case Models.Offer.UNIT.YEAR:
            return intl.formatMessage(messages[`durationYear${isPlural ? 'Plural' : 'Singular'}`], {
                years: period * cycles,
            })
        case Models.Offer.UNIT.MONTH:
            return intl.formatMessage(
                messages[`durationMonth${isPlural ? 'Plural' : 'Singular'}`],
                {
                    months: period * cycles,
                },
            )
        case Models.Offer.UNIT.WEEK:
            return intl.formatMessage(messages[`durationWeek${isPlural ? 'Plural' : 'Singular'}`], {
                weeks: period * cycles,
            })
        case Models.Offer.UNIT.DAY:
            return intl.formatMessage(messages[`durationDay${isPlural ? 'Plural' : 'Singular'}`], {
                days: period * cycles,
            })
        default:
            return formatDuration2(convertPeriodMs(period, offer.periodUnit, Models), intl)
    }
}

const isProductHd = (product: { titles: { isHD: boolean }[], contents: { isHD: boolean }[], isSubscription: () => boolean }) => {
    if (!product) return false
    if (product.isSubscription?.()) {
        return Object.values(product.titles).find((title) => title.isHD) !== undefined
    }
    return product.contents.find((content) => content.isHD) !== undefined
}

export const formatOffersPreviewPricing = (intl: IntlShape, offers: any, product: any) => {
    if (!offers) {
        return undefined
    }

    let lowestPrice: any;
    let currency
    offers.forEach((offer: { price: number, currency: string }) => {
        if (offer && offer.price && (offer.price < lowestPrice || !lowestPrice)) {
            lowestPrice = offer.price
            currency = offer.currency
        }
    })

    const finalCurrency = currency || product?.currency || ''
    const isValidCurrency = finalCurrency?.length === 3 // Examples: USD, EUR, JPY, ...
    const price = isValidCurrency
        ? intl.formatNumber(lowestPrice, {
              style: 'currency',
              currency: finalCurrency,
          })
        : `${lowestPrice} ${finalCurrency}`

    return (
        lowestPrice >= 0 &&
        intl &&
        messages.previewPrice &&
        intl.formatMessage(messages.previewPrice, {
            price,
        })
    )
}

export const createOfferLabel = (offer: any, product: any, intl: IntlShape, Models: any, config: any) => {
    if (!offer) {
        return intl?.formatMessage({id: 'NO_OFFER_AVAILABLE'})
    }

    const isHd = isProductHd(product)

    const { price, cycles, oneTimeCharge } = offer

    const currency = offer.currency || product?.currency

    const isValidCurrency = currency?.length === 3 // Examples: USD, EUR, JPY, ...

    const finalPrice = !price
        ? intl.formatMessage(messages.unknownPrice) // Unknown price should not occurs if backend well provided. Display a text to help backend provider rather then false
        : isValidCurrency
        ? intl.formatNumber(price, {
              style: 'currency',
              currency,
          })
        : `${price} ${currency}`

    const hasVideoDefinition =
        !config.disableOfferLabelWithVideoDefinition && product && !product.isSubscription?.()
    const isOneTimeSubscription = oneTimeCharge && product && product.isSubscription?.()

    const duration = formatOfferDuration(offer, intl, Models)

    const format = !config.disableOfferLabelWithVideoDefinition
        ? isHd
            ? intl.formatMessage(messages.hdFormat)
            : intl.formatMessage(messages.defaultFormat)
        : null

    if (isOneTimeSubscription) {
        return intl.formatMessage(messages.offerLabelOneTimeCharge, {
            format,
            price: finalPrice,
        })
    }

    if (cycles && cycles > 0) {
        const message = hasVideoDefinition
            ? cycles > 1
                ? messages.offerLabelWithDefinitionWithCyclePlural
                : messages.offerLabelWithDefinitionWithCycleSingular
            : cycles > 1
            ? messages.offerLabelWithCyclePlural
            : messages.offerLabelWithCycleSingular

        return intl.formatMessage(message, {
            format,
            price: finalPrice,
            cycle: cycles + 1,
            period: formatOfferUnit(offer, intl, Models), // ex: 2 months
            duration: duration, // ex: 12 months
        })
    }
    return intl.formatMessage(
        hasVideoDefinition ? messages.offerLabelWithDefinition : messages.offerLabel,
        {
            format,
            price: finalPrice,
            duration,
        },
    )
}